import React, { Suspense, lazy } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

import LoadingIndicator from "../LoadingIndicator";
import NotFound from "../NotFound";
import { fetchParentMenuItem } from "../../lib/Menu";

import { useAudioGuideApi } from "../../lib/hooks";
import { useUiState } from "../../lib/UiStates";

import css from "./DrupalPage.module.scss";

/* eslint-disable react/forbid-prop-types */

/* eslint-disable */
const AudioGuidesHomepage = lazy(() => import("../../Pages/AudioGuidesHomepage"));
const SpeakerListPage = lazy(() => import("../../Pages/SpeakerListPage"));
const AudioGuide = lazy(() => import("../AudioGuide"));
const GuideLocation = lazy(() => import("../../Pages/GuideLocation"));
const SpeakerCurator = lazy(() => import("../SpeakerCurator"));
const BasicPage = lazy(() => import("../../Pages/BasicPage"));
const KitchenSink = lazy(() => import("../../Pages/KitchenSink"));
/* eslint-enable */

const REDIRECT_MAP = {
  '/audio-guides': '/gallery-guides',
};

const getNodeType = node => {
  if (!node || !node.type) {
    return "";
  }
  return node.type[0].target_id || "";
};

const setPageTitle = (dispatch, pageTitle, pageParentTitle = "") => {
  const dispatchBundle = {
    type: "SET_PAGE_TITLE",
    pageTitle,
    pageParentTitle
  };
  dispatch(dispatchBundle);
};

const setPageNodeTitle = async (fetchByUrl, dispatch, node, location) => {
  let nodeId;
  let nodeUuid;
  let nodeType;
  let nodeTitle;
  if (node.type && node.title) {
    nodeId = node?.nid?.[0]?.value || 0;
    nodeUuid = node?.uuid?.[0]?.value || '';
    nodeType = node.type[0].target_id || "";
    nodeTitle = node.title[0].value || "";
  }
  let pageParentTitle = "";

  // @TODO Disambiguate node page from speaker list.
  if (nodeType === "audio_guide") {
    setPageTitle(dispatch, nodeTitle);
  } else if (nodeType === "guide_location") {
    const parentUuid = node.field_audio_guide[0].target_uuid || '';
    const parentNode = await fetchByUrl(`/jsonapi/node/audio_guide/${parentUuid}`);
    if (parentNode?.data?.attributes?.title) {
      pageParentTitle = parentNode.data.attributes.title || "";
      setPageTitle(dispatch, nodeTitle, pageParentTitle);
    } else {
      setPageTitle(dispatch, nodeTitle);
    }
  } else if (nodeType === "curator_speaker") {
    // Since this is linked from multiple guides,
    // we don't fetch a parent.
    setPageTitle(dispatch, nodeTitle);
  } else if (nodeType === "page") {
    const menuTree = await fetchByUrl("/api/menu_items/main");
    const parentMenuItem = fetchParentMenuItem(location, menuTree);
    if (parentMenuItem && parentMenuItem.title) {
      pageParentTitle = parentMenuItem.title;
    }
    setPageTitle(dispatch, nodeTitle, pageParentTitle);
  } else {
    setPageTitle(dispatch, "Page not found");
  }
};

const DrupalPage = ({
  location,
  modal = false,
  limitGuide = null,
  className = "",
  parentContext = {}
}) => {
  const history = useHistory();
  const { dispatch } = useUiState();
  const [loading, setLoading] = React.useState(true);
  const [locationFacts, setLocationFacts] = React.useState(null);
  const [fetchByUrl, abortFetch] = useAudioGuideApi();

  const finishLoading = () => {
    setTimeout(() => {
      setLoading(false);
    }, 800);
  };

  React.useEffect(() => {
    if (REDIRECT_MAP[location.pathname]) {
      history.push(REDIRECT_MAP[location.pathname]);
    } else {
      setLoading(true);
      if (location.pathname === "/kitchen-sink") {
        setLocationFacts({
          location: location.pathname,
          page: 'kitchenSink',
          nodeData: {},
        });
        setPageTitle(dispatch, "Kitchen Sink");
        setLoading(false);
      } else if (location.pathname === "/gallery-guides") {
        fetchByUrl("/guide/json")
          .then(json => {
            setLocationFacts({
              location: location.pathname,
              page: 'exhibitionGuidesRoot',
              nodeData: json
            });
            !modal && setPageTitle(dispatch, "All Gallery Guides");
          })
          .then(() => {
            setLoading(false);
          })
          .catch(e => {
            /* eslint-disable no-console */
            console.error(e);
            /* eslint-enable no-console */
            setLoading(false);
          });
      } else if (
        location.pathname.match(/^\/audio-guides\/([0-9]+)\/speakers$/)
      ) {
        const nodeInfo = location.pathname.match(
          /^\/audio-guides\/([0-9]+)\/speakers$/
        );
        fetchByUrl(`/node/${nodeInfo[1]}`)
          .then(json => {
            setLocationFacts({
              location: location.pathname,
              page: 'guideSpeakersList',
              nodeData: json
            });
            !modal &&
              setPageTitle(dispatch, `Speakers | ${json.title[0].value || ""}`);
          })
          .then(() => {
            setLoading(false);
          })
          .catch(e => {
            /* eslint-disable no-console */
            console.error(e);
            /* eslint-enable no-console */
            setLoading(false);
          });
      } else if (location.pathname.indexOf("/") !== 0) {
        /* eslint-disable no-console */
        console.error(`Location invalid:`, location);
        /* eslint-enable no-console */
      } else {
        fetchByUrl(location.pathname)
          .then(json => {
            setLocationFacts({
              location: location.pathname,
              page: `nodeType-${getNodeType(json)}`,
              nodeData: json
            });
            try {
              !modal && setPageNodeTitle(fetchByUrl, dispatch, json, location);
            } catch (e) {
              console.error('Could not set DrupalPage title', e);
            }
          })
          .then(finishLoading)
          .catch(e => {
            console.error(e);
            finishLoading();
          });
      }
    }
    return () => {
      abortFetch();
    };
  }, [location.pathname]);

  let InnerComponent = null;
  if (locationFacts && locationFacts.page) {
    const { page, nodeData } = locationFacts;
    if (page === 'kitchenSink') {
      InnerComponent = KitchenSink;
    } else if (page === 'exhibitionGuidesRoot') {
      InnerComponent = AudioGuidesHomepage;
    } else if (page === 'guideSpeakersList') {
      InnerComponent = SpeakerListPage;
    } else if (page === "nodeType-audio_guide") {
      InnerComponent = AudioGuide;
    } else if (page === "nodeType-guide_location") {
      InnerComponent = GuideLocation;
    } else if (page === "nodeType-curator_speaker") {
      InnerComponent = SpeakerCurator;
    } else if (page === "nodeType-page") {
      InnerComponent = BasicPage;
    }
    if (!InnerComponent) {
      /* eslint-disable no-console */
      console.warn("We don't know what kind of Drupal page this is:", nodeData);
      /* eslint-enable no-console */
    }
  }

  if (!location.pathname) {
    return [];
  }

  const loader = (
    <div className={css.loadingArea} aria-live="polite" aria-busy>
      <LoadingIndicator size="large" />
    </div>
  );

  return (
    <>
      <Suspense fallback={loader}>
        {loading ? (
          loader
        ) : (
          <>
            {InnerComponent !== null && (
              <InnerComponent
                className={className}
                node={locationFacts.nodeData || {}}
                parentContext={parentContext}
                limitGuide={limitGuide}
              />
            )}

            {!InnerComponent && (
              <div>
                <NotFound pathname={location.pathname} />
              </div>
            )}
          </>
        )}
      </Suspense>
    </>
  );
};

DrupalPage.propTypes = {
  location: PropTypes.any.isRequired,
  modal: PropTypes.bool,
  className: PropTypes.string
};

DrupalPage.defaultProps = {
  modal: false,
  className: ""
};

export default DrupalPage;
